import { useCallback } from "react"
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native"

import { router } from "expo-router"

import { BrandBanner } from "src/components/BrandBanner"
import { Button, Text } from "src/components/Ignite"
import { useChannelLink } from "src/context/ChannelLinkContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useUnauthChannelInfoQuery } from "src/queries/useUnauthChannelInfo"
import { useCleanupThenLogout } from "src/queries/useUser"
import { ThemedStyle } from "src/theme"
import { getAppVersion } from "src/utils/appVersion"
import { openLinkInBrowser } from "src/utils/openLinkInBrowser"
import { semverString } from "src/utils/stringUtils"

export default function ChannelAccessDeniedScreen() {
  const { themed } = useAppTheme()
  const { pendingChannelId, clearPendingChannelId } = useChannelLink()
  const { data: channelInfo } = useUnauthChannelInfoQuery(pendingChannelId)
  const { mutateAsync: cleanupThenLogoutAsync, isPending: isLoggingOut } = useCleanupThenLogout({
    skipChannelDeeplinkRedirect: true,
  })

  const channelImageSource = channelInfo?.brand_image ? { uri: channelInfo.brand_image } : undefined
  const channelTitle = channelInfo?.brand_title ?? channelInfo?.name ?? undefined
  const channelSubtitle = channelInfo?.brand_subtitle ?? undefined
  const channelSubscriptionUrl = channelInfo?.subscription_url ?? null

  const onRequestAccess = () => {
    if (channelSubscriptionUrl) {
      openLinkInBrowser(channelSubscriptionUrl)
    }
  }

  const onSelectAnotherChannel = useCallback(() => {
    clearPendingChannelId()
    router.replace("/setup/select-sponsor")
  }, [clearPendingChannelId])

  const onLogout = useCallback(async () => {
    clearPendingChannelId()
    await cleanupThenLogoutAsync()
    router.replace("/unauth/login")
  }, [clearPendingChannelId, cleanupThenLogoutAsync])

  return (
    <KeyboardAvoidingView
      style={themed($screenContentContainer)}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
    >
      <ScrollView
        contentContainerStyle={themed($screenContentInnerResolved)}
        showsVerticalScrollIndicator={false}
      >
        <View style={themed($titleContainer)}>
          <Text text="Login" preset="heading" style={themed($heading)} />
          <View style={$channelBanner}>
            <BrandBanner
              imageSource={channelImageSource}
              title={channelTitle}
              subtitle={channelSubtitle}
            />
          </View>
        </View>

        <View style={themed($formContainer)}>
          <Text style={themed($errorText)}>
            {`You do not currently have access to the ${channelTitle ?? "requested"} channel.`}
          </Text>

          {channelSubscriptionUrl && (
            <Button
              testID="request-access-button"
              text="Request Access"
              preset="sidekick"
              onPress={onRequestAccess}
            />
          )}

          <Button
            text="Select Another Channel"
            preset="default"
            style={themed(({ colors }) => ({
              backgroundColor: colors.buttonBackground,
              borderColor: colors.buttonBackground,
            }))}
            textStyle={themed(({ colors }) => ({ color: colors.buttonText }))}
            onPress={onSelectAnotherChannel}
          />
        </View>

        <TouchableOpacity
          style={themed($linkContainer)}
          onPress={onLogout}
          activeOpacity={0.8}
          disabled={isLoggingOut}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Text style={themed($apiUrlText)}>{isLoggingOut ? "Logging out…" : "Log Out"}</Text>
        </TouchableOpacity>

        <View style={themed($apiUrlContainer)}>
          <Text style={themed($apiUrlText)}>{"Powered by the Sidekick Platform"}</Text>
          <Text style={themed($apiUrlText)}>
            {`v${semverString({ version: getAppVersion(), build: null, sha: null })}`}
          </Text>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const $screenContentContainer: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
})

const $screenContentInner: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flexGrow: 1,
  paddingVertical: spacing.xxl,
  paddingHorizontal: spacing.lg,
})

const $screenContentInnerWeb: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flexGrow: 1,
  width: "100%",
  maxWidth: 430,
  alignSelf: "center",
  paddingVertical: spacing.xxl,
  paddingHorizontal: spacing.lg,
})

const $screenContentInnerResolved =
  Platform.OS === "web" ? $screenContentInnerWeb : $screenContentInner

const $titleContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  justifyContent: "center",
  alignItems: "center",
  marginTop: spacing.xxl,
})

const $heading: ThemedStyle<TextStyle> = ({ spacing }) => ({
  marginTop: spacing.md,
  marginBottom: spacing.sm,
})

const $channelBanner: ViewStyle =
  Platform.OS === "web"
    ? { width: "85%", alignSelf: "center" }
    : { width: "110%", alignSelf: "center" }

const $formContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  gap: spacing.md,
  marginTop: spacing.xxxl,
  marginBottom: spacing.xl,
  ...(Platform.OS === "web" ? { width: "100%" } : {}),
})

const $errorText: ThemedStyle<TextStyle> = ({ colors, spacing }) => ({
  color: colors.text,
  textAlign: "center",
  paddingHorizontal: spacing.sm,
})

const $linkContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  alignItems: "center",
  justifyContent: "center",
  paddingVertical: spacing.sm,
  marginTop: spacing.xxs,
})

const $apiUrlContainer: ThemedStyle<ViewStyle> = () => ({
  alignItems: "center",
  justifyContent: "center",
  position: "absolute",
  bottom: 20,
  left: 0,
  right: 0,
})

const $apiUrlText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.text,
  fontSize: 12,
  fontFamily: typography.secondary?.normal || typography.primary.normal,
  textAlign: "center",
})
